import { Suspense } from 'react';
import { useLingui, Trans } from '@lingui/react/macro';
import { AlertTriangleIcon } from 'lucide-react';
import { useNgoProfileQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { isUnauthorizedError } from '@/lib/ngo-session';
import { useMutation } from '@tanstack/react-query';
import { downloadFile } from '@worldcoo/ui/components/form';
import { toast } from '@/lib/toast';
import { Spinner } from '@worldcoo/ui/components/spinner';

const WORLDCOO_CONTACT_EMAIL = 'contact@worldcoo.com';

export function DraftBanner() {
	return (
		<Suspense fallback={null}>
			<DraftBannerContent />
		</Suspense>
	);
}

function DraftBannerContent() {
	const { t } = useLingui();
	const ngo = useNgoProfileQuery();

	const { mutateAsync: downloadContract, isPending } = useMutation(
		trpc.contract.downloadDraftContract.mutationOptions(),
	);

	if (!('isDraft' in ngo) || !ngo.isDraft) return null;

	const handleDownloadContract = async (e: React.MouseEvent) => {
		e.preventDefault();
		if (isPending) return;
		try {
			const result = await downloadContract();

			if (result && typeof result === 'object' && 'error' in result) {
				const errorMessages: Record<string, string> = {
					DRAFT_NOT_FOUND: t`Draft NGO not found. Please contact support.`,
					CONTRACT_GENERATION_FAILED: t`Failed to generate contract. Please try again.`,
				};
				toast.error(errorMessages[result.error as string] ?? t`Failed to generate contract`);
				return;
			}

			const base64 = result;
			const byteCharacters = atob(base64);
			const byteArray = Uint8Array.from(byteCharacters, (c) => c.charCodeAt(0));
			const blob = new Blob([byteArray], { type: 'application/pdf' });
			downloadFile(blob, `contract-${ngo.name}.pdf`);
		} catch (err) {
			if (isUnauthorizedError(err)) {
				window.location.href = '/login';
				return;
			}
			toast.error(t`Failed to generate contract`);
		}
	};

	const spinner = isPending ? <Spinner className="size-3" /> : null;

	return (
		<div className="flex items-start gap-3 border-b border-amber-200 bg-amber-50 px-6 py-3">
			<AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
			<div className="flex flex-1 flex-col gap-1">
				<p className="text-sm font-medium text-amber-900">{t`Contract pending approval`}</p>
				<p className="text-sm text-amber-700">
					<Trans>
						Projects and portals will remain in draft until your contract is approved.{' '}
						<button
							type="button"
							onClick={handleDownloadContract}
							className="inline-flex cursor-pointer items-center gap-1 font-medium underline underline-offset-2 hover:text-amber-900"
						>
							Download the contract
							{spinner}
						</button>
						, sign it, and send it to{' '}
						<a
							href={`mailto:${WORLDCOO_CONTACT_EMAIL}`}
							className="font-medium underline underline-offset-2 hover:text-amber-900"
						>
							{WORLDCOO_CONTACT_EMAIL}
						</a>
					</Trans>
				</p>
			</div>
		</div>
	);
}
